import { Box, Chip, Typography, useTheme } from '@mui/material'
import {
  gridExpandedSortedRowEntriesSelector,
  gridFilteredDescendantRowCountSelector,
  GridFilterModel,
  GridFooterContainer,
  GridPagination,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import dayjs from 'dayjs'
import { Dispatch, FC, SetStateAction, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import GridFiltersPopover from '../FiltersPopover'

interface GlobalDataGridFooterProps {
  filterModel: GridFilterModel
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>
  selectedRowCount: number
  serverSideMode: boolean
  onClearFilters?: () => void
  onFilterModelChange?: (newFilterModel: GridFilterModel) => void
  monthFilter?: string
  onChangeMonth?: (month: string) => void
}

const GlobalDataGridFooter: FC<GlobalDataGridFooterProps> = ({
  filterModel,
  setFilterModel,
  selectedRowCount,
  serverSideMode,
  onClearFilters,
  onFilterModelChange,
  monthFilter,
  onChangeMonth,
}) => {
  const theme = useTheme()
  const apiRef = useGridApiContext()
  const filtersRef = useRef<HTMLDivElement | null>(null)
  const clearRef = useRef<HTMLDivElement | null>(null)
  const moreRef = useRef<HTMLDivElement | null>(null)
  const [maxVisible, setMaxVisible] = useState(4)

  // popover state
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null)
  const openMore = Boolean(moreAnchorEl)
  const handleOpenMore = (e: React.MouseEvent<HTMLElement>) => setMoreAnchorEl(e.currentTarget)
  const handleCloseMore = () => setMoreAnchorEl(null)
  ///////////////////////////// Row Counts ///////////////////////////////////
  const getRowCounts = () => {
    if (apiRef?.current) {
      const rowCount = apiRef.current.getRowsCount()

      return rowCount
    }
  }
  const visibleRows = useGridSelector(apiRef, gridExpandedSortedRowEntriesSelector)
  const _visibleChildRowCount = useGridSelector(apiRef, gridFilteredDescendantRowCountSelector) ?? 0
  const _allRowIds = useMemo(() => {
    if (apiRef.current) return apiRef.current.getAllRowIds() as string[]
    return []
  }, [apiRef])

  const { monthlyTaskRowCount, productRowCount, taskRowCount, subtaskRowCount: _subtaskRowCount } = useMemo(() => {
    let monthlyTaskRowCount = 0
    let productRowCount = 0
    let taskRowCount = 0
    let subtaskRowCount = 0

    visibleRows.forEach(rowEntry => {
      const row = rowEntry.model // `.model` gives you the actual row data
      const sk = row?.SK || ''
      if (sk.startsWith('MONTHLY_TASK_')) {
        monthlyTaskRowCount++
      } else if (sk.startsWith('PRODUCT_')) {
        productRowCount++
      } else if (sk.startsWith('TASK_')) {
        taskRowCount++
      } else if (sk.startsWith('SUBTASK_')) {
        subtaskRowCount++
      }
    })

    return { monthlyTaskRowCount, productRowCount, taskRowCount, subtaskRowCount }
  }, [visibleRows])
  // console.log(
  //   'visibleChildRowCount',
  //   visibleChildRowCount,
  //   'monthlyTaskRowCount',
  //   monthlyTaskRowCount,
  //   'productRowCount',
  //   productRowCount,

  //   'taskRowCount',
  //   taskRowCount,
  //   'subtaskRowCount',
  //   subtaskRowCount
  // )
  const rowCount = getRowCounts()
  const monthlyTaskRowCountString =
    monthlyTaskRowCount > 0 ? `Monthly Task Rows: ${monthlyTaskRowCount} of ${(rowCount ?? 0)?.toLocaleString()}` : ''
  const taskRowCountString =
    taskRowCount > 0 ? `Task Rows: ${taskRowCount} of ${(rowCount ?? 0)?.toLocaleString()}` : ''
  const productRowCountString =
    productRowCount > 0 ? `Product Rows: ${productRowCount} of ${(rowCount ?? 0)?.toLocaleString()}` : ''
  //////////////////////////////////////////////////////////////////////////////
  const [activeFilterLabels, setActiveFilterLabels] = useState<
    { label: string; field: string; value: string | boolean; color?: string; isExcluded?: boolean }[]
  >([])
  const chipBackgroundColor = 'background.defaultdim'
  const chipHoverColor = 'background.divider'
  useEffect(() => {
    const activeLabels: {
      label: string
      field: string
      value: string | boolean
      color?: string
      isExcluded?: boolean
    }[] = []

    if (filterModel?.quickFilterValues?.length > 0) {
      filterModel.quickFilterValues.forEach(searchValue => {
        activeLabels.push({ label: searchValue, field: 'quickFilter', value: searchValue })
      })
    }

    // Add month filter as a single readable chip
    if (monthFilter && onChangeMonth) {
      activeLabels.push({
        label: `Month: ${dayjs(monthFilter).format('MMM YYYY')}`,
        field: '__monthFilter',
        value: monthFilter,
        color: undefined,
      })
    }

    filterModel?.items?.forEach(item => {
      // Skip month-range items — they're shown as the dedicated month chip above
      if (item.id?.toString().startsWith('month-range-')) return
      // Handle boolean values (false is valid), empty arrays, and null/undefined
      if (item.value === null || item.value === undefined || item.value === '') return
      if (Array.isArray(item.value) && item.value.length === 0) return

      const isArray = Array.isArray(item.value)
      const values = isArray ? item.value : [item.value]
      const isExcluded = item.operator?.toLowerCase().includes('not')

      const fieldLabelMap: Record<string, string> = {
        masterStatus: 'Status',
        dealStatus: 'Deal Status',
        type: 'Type',
        queueRole: 'Team',
        isCreative: 'Channel',
        isMarketing: 'Channel',
        isWebsite: 'Channel',
        isPostdated: 'Postdated',
      }

      const displayField = fieldLabelMap[item.field] || item.field
      const formattedDisplayField = displayField?.[0]

      values.forEach(val => {
        // Format boolean values for display
        const displayValue = typeof val === 'boolean' ? (val ? 'Yes' : 'No') : val
        activeLabels.push({
          label: `${formattedDisplayField}: ${displayValue}`,
          field: item.field,
          value: val,
          color: renderColorByString(displayValue) ?? 'text.primary',
          isExcluded,
        })
      })
    })

    setActiveFilterLabels(activeLabels)
  }, [filterModel, monthFilter, onChangeMonth])

  // 🛠️ Remove filter
  const handleDeleteFilter = (field: string, value: string | boolean) => {
    // Clear month filter via dedicated callback
    if (field === '__monthFilter') {
      onChangeMonth?.('')
      return
    }

    let newFilterModel: GridFilterModel

    if (field === 'quickFilter') {
      const updatedQuickFilters = filterModel.quickFilterValues.filter(qf => qf !== value)
      newFilterModel = { ...filterModel, quickFilterValues: updatedQuickFilters }
    } else {
      const updatedItems = filterModel.items
        .map(item => {
          if (item.field === field) {
            // 🛠️ Check if value is an array first
            if (Array.isArray(item.value)) {
              const updatedValues = item.value.filter(v => v !== value)
              // If array is now empty, remove the filter entirely
              if (updatedValues.length === 0) return null
              return { ...item, value: updatedValues }
            }

            // 🛠️ If it's not an array, remove the filter entirely
            return null
          }
          return item
        })
        .filter(Boolean) // remove nulls
      newFilterModel = { ...filterModel, items: updatedItems }
    }

    // Update local state
    setFilterModel(newFilterModel)

    // Check if all filters are now empty
    const isEmpty =
      (!newFilterModel.items || newFilterModel.items.length === 0) &&
      (!newFilterModel.quickFilterValues || newFilterModel.quickFilterValues.length === 0)

    // For server-side mode, call the appropriate callback
    if (isEmpty && onClearFilters) {
      onClearFilters()
    } else if (onFilterModelChange) {
      onFilterModelChange(newFilterModel)
    }
  }
  const clearFilters = () => {
    const emptyFilterModel = { items: [], quickFilterValues: [] }
    setFilterModel(emptyFilterModel)

    // Clear month filter too
    onChangeMonth?.('')

    // For server-side mode, notify the hook so it refetches with empty filters
    if (onFilterModelChange) {
      onFilterModelChange(emptyFilterModel)
    }

    // Call the optional clear callback (for any additional cleanup)
    if (onClearFilters) {
      onClearFilters()
    }
  }
  // Fixed max of 4 visible chips - overflow goes to "+N" popover
  const MAX_VISIBLE_CHIPS = 2

  useLayoutEffect(() => {
    // Simply cap at MAX_VISIBLE_CHIPS - no dynamic measurement needed
    setMaxVisible(Math.min(MAX_VISIBLE_CHIPS, activeFilterLabels.length))
  }, [activeFilterLabels])
  const hiddenFilters = activeFilterLabels.slice(maxVisible)
  const hiddenCount = hiddenFilters.length
  return (
    <GridFooterContainer
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1,
        bgcolor: 'background.section',
        width: '100%',
        minHeight: 52,
      }}
    >
      {/* LEFT: Pagination (server-side mode) + Row counts */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 1,
          flexShrink: 0,
          zIndex: 2, // Ensure pagination is clickable above filters
        }}
      >
        {serverSideMode && <GridPagination />}
        {/* Row counts */}
        {monthlyTaskRowCount > 0 && <Typography sx={{ fontSize: 14 }}>{monthlyTaskRowCountString}</Typography>}
        {taskRowCount > 0 && <Typography sx={{ fontSize: 14 }}>{taskRowCountString}</Typography>}
        {productRowCount > 0 && <Typography sx={{ fontSize: 14 }}>{productRowCountString}</Typography>}
        {/* Generic row count when no specialized counts apply */}
        {monthlyTaskRowCount === 0 && taskRowCount === 0 && productRowCount === 0 && !serverSideMode && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            {visibleRows.length} of {rowCount?.toLocaleString() ?? 0} rows
          </Typography>
        )}
      </Box>

      {/* RIGHT: filters lane */}
      <Box
        ref={filtersRef}
        sx={{
          flex: '0 1 auto',
          maxWidth: '50%',
          minWidth: 200,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          overflow: 'hidden',
          p: 1,
          borderRadius: 1,
          backgroundColor: theme.palette.background.section,
          justifyContent: 'flex-end',
        }}
      >
        {activeFilterLabels?.length > 0 && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0, pr: 2 }}>Filters</Typography>
        )}
        {/* visible chips */}

        {activeFilterLabels.slice(0, maxVisible).map((filter, idx) => (
          <Chip
            key={`${filter.field}-${filter.value}-${idx}`}
            data-chip
            clickable
            onClick={() => handleDeleteFilter(filter.field, filter.value)}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {filter.isExcluded && (
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.palette.text.disabled }}>🚫</Typography>
                )}
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    opacity: filter.isExcluded ? 0.4 : 1,
                    textDecoration: filter.isExcluded ? 'line-through' : 'none',
                    color: filter.color ?? renderColorByString(filter.label),
                  }}
                >
                  {filter.label}
                </Typography>
              </Box>
            }
            size="small"
            sx={{
              backgroundColor: 'background.defaultdim',
              px: 1.5,
              py: 0.5,
              flexShrink: 0,
              '&:hover': { opacity: 0.7, backgroundColor: 'background.divider' },
            }}
          />
        ))}

        {/* sticky right controls (stay at lane's right edge) */}
        <Box
          sx={{
            position: 'sticky',
            right: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            ml: 'auto',
            flexShrink: 0,
            backgroundColor: theme.palette.background.section,
          }}
        >
          {hiddenCount > 0 && (
            <Box ref={moreRef}>
              <Chip
                label={`+${hiddenCount}`}
                size="small"
                onClick={handleOpenMore}
                sx={{ fontWeight: 700, flexShrink: 0, backgroundColor: 'background.paper', cursor: 'pointer' }}
              />
            </Box>
          )}

          {activeFilterLabels.length > 0 && (
            <Box ref={clearRef}>
              <Chip
                label="Clear All"
                onClick={clearFilters}
                size="small"
                sx={{
                  backgroundColor: 'error.light',
                  color: 'white',
                  px: 1.25,
                  py: 0.5,
                  fontWeight: 700,
                  flexShrink: 0,
                  '&:hover': { opacity: 0.85 },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Popover - only render when anchorEl is valid */}
        {openMore && moreAnchorEl && (
          <GridFiltersPopover
            open={openMore}
            anchorEl={moreAnchorEl}
            closeMore={handleCloseMore}
            hiddenFilters={activeFilterLabels.slice(maxVisible)}
            setFilterModel={setFilterModel}
            deleteFilter={handleDeleteFilter}
          />
        )}
      </Box>
    </GridFooterContainer>
  )
}
export default GlobalDataGridFooter
